<template>
  <div>
    <SelectionRequiredDialog />
    <BulkReviewDialog />
    <ComplaintExportDialog />

    <BaseListPage
      headline="lang_PUBLISHING_USAGE_REVIEW"
      :context="context"
      :alvqa="alvqa"
      :stickyHeader="true"
    >
      <template v-slot:filter>
        <v-col cols="2">
          <DropdownFilter
            context="UsageReviewOutcomeFilter"
            label="lang_PUBLISHING_USAGE_CAPTION_OUTCOME"
            :items="outcomes"
            multiple
            :alvqa="alvqa + '-list-dropdown-outcome-filter'"
          />
        </v-col>
        <v-col cols="2">
          <DropdownFilter
            context="UsageReviewDecisionFilter"
            label="lang_PUBLISHING_USAGE_CAPTION_DECISION"
            :items="decisions"
            multiple
            :alvqa="alvqa + '-list-dropdown-decision-filter'"
          />
        </v-col>
        <v-col cols="2">
          <DropdownFilter
            context="UsageReviewReasonFilter"
            label="lang_PUBLISHING_USAGE_CAPTION_REASON"
            :items="reasons"
            multiple
            :alvqa="alvqa + '-list-dropdown-reason-filter'"
          />
        </v-col>
        <v-col cols="2">
          <DropdownFilter
            context="UsageReviewExportedFilter"
            label="lang_PUBLISHING_USAGE_CAPTION_EXPORTED"
            :items="exportedOptions"
            :defaultValueNull="true"
            :alvqa="alvqa + '-list-dropdown-exported-filter'"
          />
        </v-col>
      </template>

      <template v-slot:dataTable>
        <v-alert v-if="notReady" type="info" text class="mt-2" :alvqa="alvqa + '-not-ready'">
          {{ notReady.message }}
        </v-alert>
        <BaseDataTableList
          v-else
          :headers="headers"
          :context="context"
          :alvqa="alvqa"
          :hasServerSideSorting="true"
          :stickyTopSlot="true"
        >
          <template v-slot:topSlot><UsageReviewTopSlot /></template>

          <template v-slot:body="{ item }">
            <ExpandIcon :isExpanded="item.isExpanded" :alvqa="alvqa + '-list-row-' + item.item.id + '-icon-expand'" />
            <td class="no_ellipsis" :alvqa="alvqa + '-list-row-' + item.item.id + '-text-airing'">
              {{ $d(toDate(item.item.airing.date), 'alvDateFormat') }} <span class="subtitleGray--text">{{ item.item.airing.startTime.substring(0, 5) }}</span>
            </td>
            <td class="ellipsis">
              <BaseTooltip
                :simpleText="item.item.airing.stationCode || item.item.airing.stationName"
                :alvqa="alvqa + '-list-row-' + item.item.id + '-text-station'"
              />
            </td>
            <td class="ellipsis">
              <BaseTooltip :alvqa="alvqa + '-list-row-' + item.item.id + '-text-programme'">
                <template v-slot:content>
                  {{ item.item.airing.seriesTitle || item.item.airing.episodeTitle }}
                  <span v-if="item.item.airing.episode" class="subtitleGray--text">· {{ $t('lang_PUBLISHING_USAGE_CAPTION_EPISODE_SHORT') }} {{ item.item.airing.episode }}</span>
                </template>
                <template v-slot:text>{{ item.item.airing.episodeTitle }}</template>
              </BaseTooltip>
            </td>
            <td class="ellipsis">
              <BaseTooltip v-if="item.item.work" :alvqa="alvqa + '-list-row-' + item.item.id + '-text-work'">
                <template v-slot:content>
                  {{ item.item.work.title }}
                  <span class="subtitleGray--text">{{ item.item.work.gemaWorkNumber || '' }}</span>
                </template>
                <template v-slot:text>{{ item.item.work.title }} {{ item.item.work.gemaWorkNumber || '' }}</template>
              </BaseTooltip>
              <BaseTooltip
                v-else-if="item.item.workSource === 'airing'"
                :simpleText="$t('lang_PUBLISHING_USAGE_CAPTION_FINDING_NO_WORK')"
                :alvqa="alvqa + '-list-row-' + item.item.id + '-text-no-work'"
              />
              <span v-else class="subtitleGray--text">–</span>
            </td>
            <td class="no_ellipsis">
              <OutcomeChip :outcome="item.item.outcome" :alvqa="alvqa + '-list-row-' + item.item.id" />
            </td>
            <td class="ellipsis">
              <BaseTooltip
                :simpleText="$t('lang_PUBLISHING_USAGE_CAPTION_REASON_' + item.item.reason.toUpperCase())"
                :alvqa="alvqa + '-list-row-' + item.item.id + '-text-reason'"
              />
            </td>
            <td class="no_ellipsis" :alvqa="alvqa + '-list-row-' + item.item.id + '-decision'">
              <DecisionMark :finding="item.item" />
            </td>
            <td class="text-center">
              <BaseTooltip v-if="item.item.exportedAt">
                <template v-slot:content><v-icon small color="buttonNewEntity" :alvqa="alvqa + '-list-row-' + item.item.id + '-icon-exported'">send</v-icon></template>
                <template v-slot:text>{{ $t('lang_PUBLISHING_USAGE_CAPTION_REVIEW_EXPORTED_AT', { at: $d(toDate(item.item.exportedAt), 'alvDateTime') }) }}</template>
              </BaseTooltip>
            </td>
          </template>

          <template v-slot:expandSlot="{ id }"><FindingReadOnly :findingId="id" /></template>
        </BaseDataTableList>
      </template>
    </BaseListPage>
  </div>
</template>

<script>
import '../usage.css'
import BaseDataTableList from '@/components/_base/BaseDataTableList'
import BaseListPage from '@/components/_base/BaseListPage'
import BaseTooltip from '@/components/_base/BaseTooltip'
import ExpandIcon from '@/components/_buttons/ExpandIcon'
import DropdownFilter from '@/components/_filters/DropdownFilter'
import SelectionRequiredDialog from '@/components/_dialogs/SelectionRequiredDialog'

import UsageReviewTopSlot from './UsageReviewTopSlot'
import FindingReadOnly from './_readonly/FindingReadOnly'
import BulkReviewDialog from './_dialogs/BulkReviewDialog'
import ComplaintExportDialog from './_dialogs/ComplaintExportDialog'
import OutcomeChip from '../_components/OutcomeChip'
import DecisionMark from '../_components/DecisionMark'
import { OUTCOMES, DECISIONS, REASONS } from '../usageOptions'

export default {
  name: 'UsageReview',
  components: {
    BaseDataTableList,
    BaseListPage,
    BaseTooltip,
    ExpandIcon,
    DropdownFilter,
    SelectionRequiredDialog,
    UsageReviewTopSlot,
    FindingReadOnly,
    BulkReviewDialog,
    ComplaintExportDialog,
    OutcomeChip,
    DecisionMark
  },
  data() {
    return {
      alvqa: 'publishing-usage-review',
      context: 'UsageReview',
      outcomes: OUTCOMES,
      decisions: DECISIONS,
      reasons: REASONS
    }
  },
  computed: {
    notReady() { return this.$store.getters.getUsageReviewNotReady },
    exportedOptions() {
      return [
        { value: 'false', text: 'lang_PUBLISHING_USAGE_CAPTION_OPTION_NOT_EXPORTED' },
        { value: 'true', text: 'lang_PUBLISHING_USAGE_CAPTION_EXPORTED' }
      ]
    },
    headers() {
      const column = (text, value, width, sortable = true, align = 'left') => ({ text: text ? this.$t(text) : '', value, width, sortable, align, class: 'text-no-wrap' })
      return [
        column('', 'data-table-select', '60px', false, 'center'),
        column('', 'data-table-expand', '48px', false, 'center'),
        column('lang_PUBLISHING_USAGE_CAPTION_AIRING', 'airing', '150px'),
        column('lang_PUBLISHING_USAGE_CAPTION_STATION', 'station', '110px', false),
        column('lang_PUBLISHING_USAGE_CAPTION_PROGRAMME', 'programme', undefined, false),
        column('lang_PUBLISHING_CAPTION_WORK', 'work'),
        column('lang_PUBLISHING_USAGE_CAPTION_OUTCOME', 'outcome', '150px'),
        column('lang_PUBLISHING_USAGE_CAPTION_REASON', 'reason', '220px'),
        column('lang_PUBLISHING_USAGE_CAPTION_DECISION', 'decision', '130px'),
        column('', 'exported', '50px', false, 'center')
      ]
    }
  },
  methods: {
    toDate(value) { return new Date(String(value).replace(' ', 'T')) }
  },
  watch: {
    '$route.params.alvId': {
      immediate: true,
      async handler(id) {
        await this.$store.dispatch('openUsageReview', Number(id))
      }
    }
  }
}
</script>
